import { Inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import {
  OrderApi,
  PaxApi,
  OrderDTO,
  TerminalDTO,
  TransactionStatusResponseDTO,
} from '@lkp/parking-sdk';
import { ORDER_API, PAX_API } from '../../providers/api.module';
import * as Actions from './orders.action';
import { normalizeErrorMessage } from 'src/app/core/utils/error-normalize';
import {
  Subject,
  first,
  map,
  of,
  switchMap,
  takeUntil,
  tap,
  timer,
  Observable,
  exhaustMap,
  concatMap,
} from 'rxjs';

export interface OrderStateModel {
  order: OrderDTO;
  newOrder: boolean;
  status: TransactionStatusResponseDTO;
  terminal: TerminalDTO;
  uti?: string;
}

@State<OrderStateModel>({
  name: 'order',
  defaults: {
    order: {},
    newOrder: true,
    status: {},
    terminal: {},
    uti: undefined,
  },
})
@Injectable()
export class OrdersState {
  private abortTimer: Subject<void> = new Subject();
  constructor(
    @Inject(ORDER_API) readonly orderApi: OrderApi,
    @Inject(PAX_API) readonly paxApi: PaxApi
  ) {}

  @Selector()
  static order(state: OrderStateModel) {
    return state.order;
  }

  @Selector()
  static newOrder(state: OrderStateModel) {
    return state.newOrder;
  }

  @Selector()
  static status(state: OrderStateModel) {
    return state.status;
  }

  @Selector()
  static terminal(state: OrderStateModel) {
    return state.terminal;
  }

  @Action(Actions.GetOrder)
  getOrder(context: StateContext<OrderStateModel>, action: Actions.GetOrder) {
    const { orderId } = action;
    return this.orderApi.getOrdersAdmin({ size: 1, orderId }).pipe(
      tap((orders) => {
        if (orders?.content?.[0])
          context.patchState({ order: orders.content[0], newOrder: false });
      })
    );
  }

  @Action(Actions.CreateOrder)
  createOrder(context: StateContext<OrderStateModel>) {
    return this.orderApi.createOrderAdmin().pipe(
      tap({
        next: (newOrder) => {
          context.patchState({ order: newOrder, newOrder: true });
        },
        error: (error) =>
          console.error('Error creating order in state create order: ', error),
      })
    );
  }

  @Action(Actions.AddOrderItem)
  addOrderItem(
    context: StateContext<OrderStateModel>,
    action: Actions.AddOrderItem
  ) {
    const { orderItem } = action;
    const orderId = context.getState().order.orderId;

    /* If there is an existing order */
    if (orderId) {
      return this.orderApi
        .addOrderItemAdmin({ orderId, orderItemDTO: orderItem })
        .pipe(
          tap({
            next: (order) => {
              context.patchState({ order });
            },
            error: (error) =>
              console.error(
                'Error adding order item: ',
                normalizeErrorMessage(error),
                error
              ),
          })
        );
    }

    return this.createOrder(context).pipe(
      concatMap((newOrder) => {
        return newOrder.orderId
          ? this.orderApi
              .addOrderItemAdmin({
                orderId: newOrder.orderId,
                orderItemDTO: orderItem,
              })
              .pipe(
                tap({
                  next: (order) => {
                    context.patchState({ order });
                    return order;
                  },
                  error: (error) =>
                    console.error(
                      'Error adding order item in state: ',
                      normalizeErrorMessage(error),
                      error
                    ),
                })
              )
          : of(newOrder);
      })
    );
  }

  @Action(Actions.UpdateOrderItem)
  updateOrderItem(
    context: StateContext<OrderStateModel>,
    action: Actions.UpdateOrderItem
  ): Observable<OrderDTO> {
    const { orderId, orderItemId, orderItem } = action;

    return this.orderApi
      .updateOrderItemAdmin({ orderId, orderItemId, orderItemDTO: orderItem })
      .pipe(
        tap({
          next: (order) => {
            context.patchState({ order: order });
          },
          error: (error) =>
            console.error(
              'Error updating order item: ',
              normalizeErrorMessage(error),
              error
            ),
        })
      );
  }

  @Action(Actions.UpdateOrder)
  updateOrder(
    context: StateContext<OrderStateModel>,
    action: Actions.UpdateOrder
  ) {
    const { orderId, order } = action;
    return this.orderApi.updateOrderAdmin({ orderId, orderDTO: order }).pipe(
      tap({
        next: (updatedOrder) => {
          context.patchState({ order: updatedOrder });
        },
        error: (error) => {
          console.error(
            'Error updating order in state: ',
            normalizeErrorMessage(error),
            error
          );
        },
      })
    );
  }

  @Action(Actions.SaveTerminal)
  saveTerminal(
    context: StateContext<OrderStateModel>,
    action: Actions.SaveTerminal
  ) {
    const { terminal } = action;

    return context.patchState({ terminal: terminal });
  }

  @Action(Actions.CheckoutOrderPax)
  checkoutOrder(
    context: StateContext<OrderStateModel>,
    action: Actions.CheckoutOrderPax
  ) {
    const { orderId, payment } = action;

    this.abortTimer = new Subject<void>();

    return this.orderApi
      .checkoutOrderAdmin({ orderId, paymentDTO: payment })
      .pipe(
        first(),
        switchMap((response) => {
          if (response.uti) {
            let uti: string = response.uti;
            context.patchState({ uti });
            return timer(0, 1000).pipe(
              takeUntil(this.abortTimer),
              exhaustMap(() =>
                this.orderApi.getCheckoutStatusAdmin({ orderId, uti }).pipe(
                  takeUntil(this.abortTimer),
                  map((status) => {
                    context.patchState({ status: status });
                    if (status.transApproved) {
                      this.abortTimer.next();
                      this.abortTimer.complete();
                    }
                    if (status.transDeclined) {
                      this.abortTimer.next();
                      this.abortTimer.complete();
                    }
                    if (status.transCancelled) {
                      this.abortTimer.next();
                      this.abortTimer.complete();
                    }
                    return status;
                  })
                )
              )
            );
          } else {
            return of();
          }
        })
      )
      .subscribe({
        next: () => {},
        error: (error) => {
          context.patchState({ status: { errorText: 'no connection' } });
          console.error('Error card payment: ', error);
        },
      });
  }

  @Action(Actions.CheckoutOrderCash)
  checkoutOrderCash(
    context: StateContext<OrderStateModel>,
    action: Actions.CheckoutOrderCash
  ) {
    const { orderId, payment } = action;

    return this.orderApi.checkoutOrderAdmin({ orderId, paymentDTO: payment });
  }

  @Action(Actions.CheckoutOrderPayLater)
  checkoutOrderPayLater(
    context: StateContext<OrderStateModel>,
    action: Actions.CheckoutOrderPayLater
  ) {
    const { orderId } = action;

    return this.orderApi.checkoutOrderNoPayAdmin({ orderId });
  }

  @Action(Actions.Refund)
  handleRefund(context: StateContext<OrderStateModel>, action: Actions.Refund) {
    const { orderId, orderItemId, paymentId, payment } = action;

    this.abortTimer = new Subject<void>();

    if (payment.paymentMethod?.paymentMethodId !== 'PAX') {
      return this.orderApi
        .refundOrderAdmin({
          orderId,
          orderItemId,
          paymentId,
          paymentDTO: payment,
        })
        .pipe(
          tap({
            next: () => {},
            error: (error) => {
              console.error(error);
            },
          })
        );
    } else {
      return this.orderApi
        .refundOrderAdmin({
          orderId,
          orderItemId,
          paymentId,
          paymentDTO: payment,
        })
        .pipe(
          first(),
          switchMap((response) => {
            if (response.uti) {
              let uti: string = response.uti;
              return timer(0, 1000).pipe(
                takeUntil(this.abortTimer),
                exhaustMap(() =>
                  this.orderApi.getCheckoutStatusAdmin({ orderId, uti }).pipe(
                    map((status) => {
                      context.patchState({ status: status });

                      if (status.transApproved) {
                        this.abortTimer.next();
                        this.abortTimer.complete();
                      }
                      if (status.transCancelled) {
                        this.abortTimer.next();
                        this.abortTimer.complete();
                      }
                      if (status.transDeclined) {
                        this.abortTimer.next();
                        this.abortTimer.complete();
                      }
                      return status;
                    })
                  )
                )
              );
            } else {
              return of();
            }
          })
        )
        .subscribe({
          next: () => {},
          error: (error) => {
            context.patchState({ status: { errorText: 'no connection' } });
            console.error('Error card payment: ', error);
          },
        });
    }
  }

  @Action(Actions.CancelCheckout)
  cancelCheckout(context: StateContext<OrderStateModel>) {
    const { order, uti } = context.getState();
    const orderId = order.orderId;

    if (!orderId || !uti) {
      console.error(
        'Error when cancelling transaction: missing orderId or uti.'
      );
      this.abortTimer.next();
      this.abortTimer.complete();
      return of(undefined);
    }

    return this.orderApi.cancelRapydTransactionAdmin({ orderId, uti }).pipe(
      tap({
        next: () => {
          this.abortTimer.next();
          this.abortTimer.complete();
        },
        error: (error) => {
          console.error('Error when cancelling transaction: ', error);
          this.abortTimer.next();
          this.abortTimer.complete();
        },
      })
    );
  }

  @Action(Actions.DeleteOrderItem)
  deleteOrderItem(
    context: StateContext<OrderStateModel>,
    action: Actions.DeleteOrderItem
  ) {
    const { orderId, orderItemId } = action;

    return this.orderApi.deleteOrderItemAdmin({ orderId, orderItemId }).pipe(
      switchMap(() => {
        const currentState = context.getState();

        const updatedOrderItems = currentState.order.orderItems!.filter(
          (item) => item.orderItemId !== orderItemId
        );

        const updatedOrder = {
          ...currentState.order,
          orderItems: [...updatedOrderItems],
        };

        return context.dispatch(new Actions.UpdateOrder(orderId, updatedOrder));
      })
    );
  }

  @Action(Actions.DeleteOrder)
  deleteOrder(
    context: StateContext<OrderStateModel>,
    action: Actions.DeleteOrder
  ) {
    const { orderId } = action;

    return this.orderApi.deleteOrderAdmin({ orderId }).pipe(
      tap(() => {
        this.resetState(context);
      })
    );
  }

  @Action(Actions.ResetOrderTransState)
  resetOrderTransState(context: StateContext<OrderStateModel>) {
    context.patchState({
      status: {},
      uti: undefined,
    });
  }

  @Action(Actions.ResetOrderState)
  resetState(context: StateContext<OrderStateModel>) {
    context.setState({
      order: {},
      newOrder: context.getState().newOrder,
      status: {},
      terminal: context.getState().terminal,
      uti: undefined,
    });
  }
}
